import type { Character, Location, Objective, SideQuest, Skill } from "@/types/story.ts";

export type ThemeContent = {
  storyNames: string[];
  objectives: Objective[];
  storyTwists: string[];
  sideQuests: SideQuest[];
  skills: Skill[];
  characters: Character[];
  storyIntros: string[];
  locations: Location[];
};

type ThemeMeta = {
  emoji: string;
  description: string;
  keywords: string[];
};

const objective = (description: string, skillUses: string[]): Objective => ({
  description,
  skillUses,
});

const sideQuest = (description: string, skillUses: string[]): SideQuest => ({
  description,
  skillUses,
});

const skill = (name: string, emoji: string, application: string): Skill => ({
  name,
  emoji,
  application,
});

const character = (name: string, role: string, emoji: string, skillUses: string[]): Character => ({
  name,
  role,
  emoji,
  skillUses,
});

const location = (
  name: string,
  description: string,
  emoji: string,
  skillUses: string[],
): Location => ({
  name,
  description,
  emoji,
  skillUses,
});

const THEME_META: Record<string, ThemeMeta> = {
  "Talking Animal Tales": {
    emoji: "🐾",
    description: "Animals build, talk, and adventure",
    keywords: ["animal", "pet", "talking"],
  },
  "Knights & Dragons": {
    emoji: "⚔️",
    description: "Kingdom quests and dragon trials",
    keywords: ["knight", "dragon", "kingdom", "castle"],
  },
  "Magic School": {
    emoji: "🏰",
    description: "Wizard lessons and magical mishaps",
    keywords: ["magic", "wizard", "school", "spell"],
  },
  "Deep Blue": {
    emoji: "🧜",
    description: "Mermaids and undersea diplomacy",
    keywords: ["deep", "blue", "underwater", "ocean", "mermaid"],
  },
  "Lost Worlds": {
    emoji: "🦖",
    description: "Ruins, jungles, and dinosaurs",
    keywords: ["lost", "world", "dinosaur", "ruin"],
  },
  Superheroes: {
    emoji: "🦸",
    description: "Masks, capes, and city rescues",
    keywords: ["hero", "super", "cape", "villain"],
  },
  "Alien Planet": {
    emoji: "🛸",
    description: "Extraterrestrial creatures and mysteries",
    keywords: ["alien", "planet", "extraterrestrial", "space"],
  },
  "Wild West": {
    emoji: "🤠",
    description: "Frontier towns and gold rush trails",
    keywords: ["west", "frontier", "sheriff", "cowboy"],
  },
  "Monster Hunters": {
    emoji: "🧟",
    description: "Creepy caverns and curious creatures",
    keywords: ["monster", "hunter", "cryptid", "cavern"],
  },
  "Tiny World": {
    emoji: "🐜",
    description: "Big adventures at tiny scale",
    keywords: ["tiny", "bug", "mini", "backyard"],
  },
  "Pet Detective": {
    emoji: "🐾",
    description: "Animal sleuths cracking clever cases",
    keywords: ["pet", "detective", "mystery", "animal"],
  },
  "Arctic Expedition": {
    emoji: "🏔️",
    description: "Frozen journeys and icy treasures",
    keywords: ["arctic", "ice", "snow", "expedition"],
  },
  "Robot City": {
    emoji: "🤖",
    description: "Circuit streets and helpful bots",
    keywords: ["robot", "city", "circuit", "future"],
  },
  "Steampunk Voyages": {
    emoji: "⚓",
    description: "Airships and clockwork exploration",
    keywords: ["steampunk", "airship", "clockwork", "voyage"],
  },
  "Mad Lab": {
    emoji: "🧪",
    description: "Wild inventions and science chaos",
    keywords: ["lab", "mad", "science", "invention"],
  },
  "Time Tangles": {
    emoji: "⏳",
    description: "Paradoxes, loops, and history knots",
    keywords: ["time", "loop", "paradox", "history"],
  },
  "Cyberpunk Streets": {
    emoji: "💾",
    description: "Neon districts and digital heists",
    keywords: ["cyber", "neon", "hacker", "digital"],
  },
  "Secret Agents": {
    emoji: "🕶️",
    description: "Spy gadgets and covert missions",
    keywords: ["agent", "spy", "secret", "mission"],
  },
};

const keywordMeta = Object.values(THEME_META).flatMap((meta) =>
  meta.keywords.map((keyword) => ({ keyword, emoji: meta.emoji, description: meta.description }))
);

export function getThemeMeta(theme: string): { emoji: string; description: string } {
  const exactMatch = THEME_META[theme];
  if (exactMatch) {
    return { emoji: exactMatch.emoji, description: exactMatch.description };
  }

  const key = theme.toLowerCase();
  const fuzzyMatch = keywordMeta.find((item) => key.includes(item.keyword));

  return fuzzyMatch
    ? { emoji: fuzzyMatch.emoji, description: fuzzyMatch.description }
    : { emoji: "✨", description: `A ${theme.toLowerCase()} adventure` };
}

export const THEME_CONTENT: Record<string, ThemeContent> = {
  "Talking Animal Tales": {
    storyNames: [
      "The Barkside Builders",
      "Parliament of Paws",
      "Featherlight Rescue",
      "The Great Acorn Pact",
    ],
    objectives: [
      objective("Unite four animal neighborhoods to rebuild the broken sky bridge", [
        "Use Teamwork to organize species crews",
        "Use Problem Solving to design safe supports",
        "Use Kindness to settle old rivalries",
      ]),
      objective("Recover the stolen town bell before the moon festival", [
        "Use Tracking to follow muddy prints",
        "Use Sneaking to enter the junkyard maze",
        "Use Negotiation to trade for clues",
      ]),
      objective("Protect the woodland council from a surprise storm", [
        "Use Engineering to reinforce shelters",
        "Use Communication to guide evacuations",
        "Use Bravery to cross flooded paths",
      ]),
    ],
    storyTwists: [
      "The thief was hiding the bell to stop a larger danger",
      "The bridge map is written in migrating bird songs",
      "The smallest character knows a forgotten tunnel",
      "A rival pack has secretly been helping all along",
    ],
    sideQuests: [
      sideQuest("Deliver medicine through fox tunnels", [
        "Use Navigation underground",
        "Use Trust to gain tunnel access",
      ]),
      sideQuest("Calm a panicked herd near the watermill", [
        "Use Empathy to understand fear",
        "Use Rhythm to lead a safe route",
      ]),
      sideQuest("Find missing hatchlings at dusk", [
        "Use Observation in tall grass",
        "Use Calling Signals to gather allies",
      ]),
    ],
    skills: [
      skill("Teamwork", "🤝", "Coordinate mixed-species missions quickly"),
      skill("Tracking", "👣", "Read claw marks and scent trails"),
      skill("Nest Engineering", "🛠️", "Build safe shelters and bridges"),
      skill("Sneaking", "🥷", "Move silently through brush and roots"),
      skill("Animal Diplomacy", "🗣️", "Resolve disputes between groups"),
      skill("Courage", "🦁", "Lead rescues in dangerous weather"),
    ],
    characters: [
      character("Mayor Mox", "Wise badger city planner", "🦡", [
        "Reads old town maps",
        "Calms tense negotiations",
      ]),
      character("Pip", "Sparrow courier", "🐦", ["Scouts from above", "Delivers urgent notes"]),
      character("Tilda", "Inventor beaver", "🦫", [
        "Repairs waterworks fast",
        "Designs sturdy structures",
      ]),
      character("Rook", "Street-smart raccoon", "🦝", [
        "Finds hidden shortcuts",
        "Opens tricky locks",
      ]),
    ],
    storyIntros: [
      "At sunrise, every animal in Briar Borough hears the town bell vanish mid-ring. Without it, the seasonal truce expires by nightfall.",
      "A letter stamped with a pawprint crest arrives at your den: the river bridge has cracked, and only a joint crew can fix it before migration.",
    ],
    locations: [
      location("Briar Borough", "A bustling animal town built into giant roots", "🌳", [
        "Gather volunteers",
        "Collect shared tools",
      ]),
      location("Sky Bridge", "A rope-and-branch bridge between oak towers", "🌉", [
        "Repair support knots",
        "Direct safe crossings",
      ]),
      location("Moonseed Market", "A night bazaar lit by fireflies", "🪔", [
        "Trade for parts",
        "Listen for rumors",
      ]),
      location("Whistle Caves", "Echoing tunnels under the meadow", "🕳️", [
        "Follow sound clues",
        "Avoid collapses",
      ]),
    ],
  },
  "Knights & Dragons": {
    storyNames: [
      "Banner of Emberkeep",
      "The Troll Toll",
      "Crown of the North Gate",
      "Dragon Over Brighthold",
    ],
    objectives: [
      objective("Cross three kingdoms to recover a stolen peace treaty", [
        "Use Horsemanship on long roads",
        "Use Diplomacy with rival lords",
        "Use Swordplay when ambushed",
      ]),
      objective("Defeat the bridge trolls blocking grain caravans", [
        "Use Tactics to split enemy groups",
        "Use Shield Work to hold the line",
        "Use Negotiation to avoid needless battle",
      ]),
      objective("Earn the trust of an ancient dragon guardian", [
        "Use Lore to interpret dragon law",
        "Use Bravery to enter the mountain court",
        "Use Honesty during trial questions",
      ]),
    ],
    storyTwists: [
      "The dragon has been guarding the kingdom's true heir",
      "The treaty thief works for a trusted advisor",
      "One troll clan wants peace and safe passage",
      "The treasure chest contains maps, not gold",
    ],
    sideQuests: [
      sideQuest("Repair the watchtower beacon before night", [
        "Use Masonry basics",
        "Use Signal code",
      ]),
      sideQuest("Escort villagers through wolf pass", [
        "Use Shield Wall formation",
        "Use Calm leadership",
      ]),
      sideQuest("Win a joust for alliance rights", ["Use Lance control", "Read opponent timing"]),
    ],
    skills: [
      skill("Swordplay", "⚔️", "Duel in close combat with discipline"),
      skill("Shield Work", "🛡️", "Protect allies in narrow passages"),
      skill("Dragon Lore", "📜", "Read ancient runes and oaths"),
      skill("Horsemanship", "🐎", "Travel and maneuver in battle"),
      skill("Castle Tactics", "🏰", "Plan sieges and defensive routes"),
      skill("Royal Diplomacy", "🤝", "Negotiate truces between houses"),
    ],
    characters: [
      character("Sir Elric", "Young knight with a strict code", "🧑‍⚔️", [
        "Leads charge lines",
        "Knows border forts",
      ]),
      character("Lady Mirel", "Diplomat from the river court", "👸", [
        "Settles disputes",
        "Translates court customs",
      ]),
      character("Bramm", "Reformed troll blacksmith", "🔨", [
        "Forges sturdy gear",
        "Knows troll tunnels",
      ]),
      character("Aurelion", "Ancient dragon judge", "🐉", [
        "Tests hero intent",
        "Reveals hidden truths",
      ]),
    ],
    storyIntros: [
      "At dawn, a dragon shadow crosses the castle walls, and the treaty chest is gone from the royal vault.",
      "The king's road stands silent: troll banners now hang from the bridge, and no caravan has returned in days.",
    ],
    locations: [
      location("Emberkeep", "A hilltop fortress with flaming beacons", "🏰", [
        "Gather royal orders",
        "Resupply armor",
      ]),
      location("Trollbridge", "A stone crossing over misty gorge", "🌉", [
        "Negotiate passage",
        "Plan a flanking route",
      ]),
      location("Ashen Peaks", "Dragon-haunted volcanic mountains", "🌋", [
        "Track heat vents",
        "Find hidden roosts",
      ]),
      location("Oakshield Village", "A frontier town under pressure", "🏘️", [
        "Protect civilians",
        "Recruit local scouts",
      ]),
    ],
  },
  "Magic School": {
    storyNames: ["Bell of the Seventh Hall", "Potion Panic", "Midnight Familiar", "The Wand Trial"],
    objectives: [
      objective("Stop runaway classroom spells before Founders Day", [
        "Use Spell Control to stabilize charms",
        "Use Team Casting for stronger wards",
        "Use Logic to identify spell loops",
      ]),
      objective("Recover the missing grimoire from hidden stacks", [
        "Use Rune Reading to decode shelf marks",
        "Use Stealth to avoid sentinel books",
        "Use Light Magic in maze corridors",
      ]),
      objective("Repair the school's protective barrier", [
        "Use Arcane Math to tune crystal nodes",
        "Use Potion Craft for conductors",
        "Use Leadership to assign teams",
      ]),
    ],
    storyTwists: [
      "The missing grimoire is choosing a new guardian",
      "A prank spell reveals a real hidden chamber",
      "The strict headmaster is protecting a secret portal",
      "The familiar causing trouble is trying to warn students",
    ],
    sideQuests: [
      sideQuest("Catch escaped broomsticks in the quad", [
        "Use Levitation safely",
        "Use Coordination drills",
      ]),
      sideQuest("Help first-years with potion antidotes", [
        "Use Herb knowledge",
        "Use precise timing",
      ]),
      sideQuest("Translate a ghost professor's lecture notes", [
        "Use Rune Reading",
        "Ask library spirits",
      ]),
    ],
    skills: [
      skill("Spell Control", "✨", "Cast precise magic without backlash"),
      skill("Potion Craft", "🧪", "Brew effects and counteragents"),
      skill("Rune Reading", "🔣", "Decode ancient spell inscriptions"),
      skill("Familiar Bonding", "🦉", "Coordinate with magical companions"),
      skill("Arcane Math", "📐", "Balance magical energy systems"),
      skill("Ward Weaving", "🪄", "Build protective barriers quickly"),
    ],
    characters: [
      character("Professor Thorne", "Strict but fair transmutation teacher", "🧙", [
        "Explains unstable magic",
        "Knows vault keys",
      ]),
      character("Nia", "Top student inventor", "📚", [
        "Builds spell gadgets",
        "Finds library shortcuts",
      ]),
      character("Grizzle", "Dorm ghost prefect", "👻", [
        "Passes through locked doors",
        "Remembers old floor plans",
      ]),
      character("Ember", "Fox familiar with attitude", "🦊", [
        "Tracks magical scent trails",
        "Finds hidden stash rooms",
      ]),
    ],
    storyIntros: [
      "Just before first bell, every wand in Seventh Hall sparks at once, drawing the same symbol in the air.",
      "The school's protective dome flickers, and glowing notes scatter from the library like startled birds.",
    ],
    locations: [
      location("Seventh Hall", "A vaulted classroom full of moving desks", "🏫", [
        "Contain spell surges",
        "Inspect glyph etchings",
      ]),
      location("Moonglass Library", "Towering stacks with shifting staircases", "📚", [
        "Search restricted shelves",
        "Consult archive spirits",
      ]),
      location("Potion Atrium", "Greenhouse-lab hybrid with bubbling stations", "🧪", [
        "Brew stabilizers",
        "Harvest ingredients",
      ]),
      location("North Ward Gate", "Crystal arch that powers campus shields", "🛡️", [
        "Retune barrier nodes",
        "Repel intrusions",
      ]),
    ],
  },
  "Deep Blue": {
    storyNames: [
      "Coral Crown Council",
      "Song of the Kelp Roads",
      "Pearl Treaty",
      "Storm Above, Peace Below",
    ],
    objectives: [
      objective("Broker peace between reef clans and trench guardians", [
        "Use Diplomacy at council meetings",
        "Use Ocean Lore to interpret customs",
        "Use Navigation through shifting currents",
      ]),
      objective("Recover the tide crystal stolen from the coral city", [
        "Use Swimming to pursue the raiders",
        "Use Tracking in sand channels",
        "Use Stealth near eel patrols",
      ]),
      objective("Stop toxic runoff before it reaches hatchery reefs", [
        "Use Engineering to reroute flows",
        "Use Teamwork for cleanup lines",
        "Use Signaling to warn nearby towns",
      ]),
    ],
    storyTwists: [
      "The raiders stole the crystal to power medical domes",
      "A surface storm is being caused by a broken deep machine",
      "The loudest council rival is secretly your strongest ally",
      "Ancient whale songs are actually map coordinates",
    ],
    sideQuests: [
      sideQuest("Escort turtle caravans through shark waters", [
        "Use Formation swimming",
        "Use Sound beacons",
      ]),
      sideQuest("Repair cracked dome glass in a nursery", [
        "Use Sealant craft",
        "Use Pressure control",
      ]),
      sideQuest("Retrieve lost pearls from a whirlpool", [
        "Use Rope lines",
        "Use Timing with tides",
      ]),
    ],
    skills: [
      skill("Currents Navigation", "🧭", "Read tides and underwater lanes"),
      skill("Ocean Diplomacy", "🤝", "Negotiate between sea factions"),
      skill("Deep Diving", "🤿", "Operate safely in pressure zones"),
      skill("Coral Engineering", "🪸", "Repair reefs and sea structures"),
      skill("Echo Signaling", "📣", "Communicate over long underwater distances"),
      skill("Trident Defense", "🔱", "Defend allies from predators"),
    ],
    characters: [
      character("Lyra", "Mermaid envoy from Sunreef", "🧜", [
        "Leads treaty talks",
        "Knows council politics",
      ]),
      character("Brine", "Crab engineer", "🦀", ["Fixes pressure valves", "Builds reef braces"]),
      character("Old Fin", "Whale historian", "🐋", [
        "Interprets song archives",
        "Knows deep routes",
      ]),
      character("Rill", "Fast scout seahorse rider", "🐠", [
        "Carries urgent messages",
        "Tracks raider wakes",
      ]),
    ],
    storyIntros: [
      "In the coral parliament, every lantern goes dark as the tide crystal disappears from its pedestal.",
      "A message shell arrives from the trench: negotiations collapse unless someone returns before moon-tide.",
    ],
    locations: [
      location("Sunreef City", "A bright coral capital of towers and domes", "🪸", [
        "Address council disputes",
        "Gather divers",
      ]),
      location("Kelp Roads", "Forest-like sea lanes between kingdoms", "🌿", [
        "Navigate currents",
        "Avoid hidden nets",
      ]),
      location("Azure Trench Gate", "A deep-sea checkpoint with glowing runes", "🌌", [
        "Use pressure suits",
        "Decode gate markings",
      ]),
      location("Pearl Hatchery", "Protected nurseries for young sea life", "🫧", [
        "Reinforce barriers",
        "Evacuate safely",
      ]),
    ],
  },
  "Lost Worlds": {
    storyNames: [
      "Valley Beyond the Mist",
      "Ruins of Thunderstone",
      "Clawmark Compass",
      "The Last Sky Temple",
    ],
    objectives: [
      objective("Find the hidden valley before rival explorers arrive", [
        "Use Cartography to decode old maps",
        "Use Climbing on canyon walls",
        "Use Survival to cross wild terrain",
      ]),
      objective("Protect villagers from roaming dinosaurs", [
        "Use Observation to predict movement",
        "Use Traps to redirect safely",
        "Use Courage to hold defensive lines",
      ]),
      objective("Recover the sky temple key from collapsed ruins", [
        "Use Puzzle Solving on stone locks",
        "Use Team Lifting for debris",
        "Use Torchcraft in dark chambers",
      ]),
    ],
    storyTwists: [
      "The fiercest dinosaur is guarding hatchlings",
      "The rival expedition is searching for medicine, not gold",
      "Temple guardians respond to music rather than force",
      "The old map points to a moving location",
    ],
    sideQuests: [
      sideQuest("Rescue trapped surveyors from a sinkhole", [
        "Use Rope descent",
        "Stabilize loose rock",
      ]),
      sideQuest("Retrieve fossil samples before rain", [
        "Use careful excavation",
        "Build a shelter frame",
      ]),
      sideQuest("Repair the valley signal tower", ["Use pulley systems", "Align mirror panels"]),
    ],
    skills: [
      skill("Cartography", "🗺️", "Read and redraw ancient maps"),
      skill("Expedition Survival", "🏕️", "Travel safely through remote terrain"),
      skill("Fossil Lore", "🦴", "Interpret clues from ancient remains"),
      skill("Climbing", "🧗", "Scale cliffs and broken temples"),
      skill("Field Engineering", "🔧", "Build camps and repair gear"),
      skill("Creature Handling", "🦕", "Calm or redirect giant beasts"),
    ],
    characters: [
      character("Captain Inez", "Seasoned expedition leader", "🧭", [
        "Sets safe routes",
        "Reads weather signs",
      ]),
      character("Koa", "Ruins linguist", "📜", ["Deciphers carvings", "Unlocks puzzle doors"]),
      character("Milo", "Young fossil enthusiast", "🦴", [
        "Finds hidden artifacts",
        "Spots fake relics",
      ]),
      character("Tara", "Rival explorer with a conscience", "🥾", [
        "Knows alternate trails",
        "Shares urgent intel",
      ]),
    ],
    storyIntros: [
      "The jungle mist parts for a heartbeat, revealing a valley no map has ever marked.",
      "At campfire dawn, a giant footprint appears beside your tent and points toward ancient ruins.",
    ],
    locations: [
      location("Thunderstone Ruins", "Crumbling temples wrapped in vines", "🛕", [
        "Solve stone mechanisms",
        "Avoid trap floors",
      ]),
      location("Mistgate Valley", "A hidden basin with roaming giants", "🌫️", [
        "Track safe routes",
        "Set observation posts",
      ]),
      location("Sky Temple", "A cliffside sanctuary above the clouds", "🏔️", [
        "Climb narrow ledges",
        "Restore wind bridges",
      ]),
      location("Fossil Flats", "Open badlands rich with ancient bones", "🦖", [
        "Excavate carefully",
        "Watch for predators",
      ]),
    ],
  },
  Superheroes: {
    storyNames: ["Midnight Siren", "Metroline Meltdown", "Capes Over Harbor", "The Masked Signal"],
    objectives: [
      objective("Stop a citywide blackout caused by villain tech", [
        "Use Gadgetry to reroute power",
        "Use Team Coordination across districts",
        "Use Stealth to reach control hubs",
      ]),
      objective("Rescue civilians trapped in a collapsing sky rail", [
        "Use Strength for debris removal",
        "Use Speed for rapid evacuations",
        "Use Leadership to prevent panic",
      ]),
      objective("Unmask the mastermind behind synchronized crimes", [
        "Use Investigation to connect clues",
        "Use Cyber Forensics on villain feeds",
        "Use Disguise for undercover work",
      ]),
    ],
    storyTwists: [
      "The villain is trying to expose corruption, not destroy the city",
      "A trusted sponsor funded the threat tech",
      "Your old training partner has been framed",
      "The final clue is hidden in fan messages",
    ],
    sideQuests: [
      sideQuest("Recover medicine from looted clinic vans", [
        "Use Pursuit driving",
        "Use Crowd control",
      ]),
      sideQuest("Disarm drone swarm near city hall", ["Use EMP timing", "Use rooftop movement"]),
      sideQuest("Protect witnesses at night court", [
        "Use Perimeter planning",
        "Use Non-lethal defense",
      ]),
    ],
    skills: [
      skill("Heroic Coordination", "🦸", "Combine powers for safer outcomes"),
      skill("Urban Mobility", "🧗", "Traverse rooftops and high-rises fast"),
      skill("Gadgetry", "🛠️", "Build and deploy mission tools"),
      skill("Crisis Leadership", "📢", "Direct civilians under pressure"),
      skill("Forensic Analysis", "🔍", "Turn crime-scene details into leads"),
      skill("Defensive Combat", "🥊", "Neutralize threats without harming bystanders"),
    ],
    characters: [
      character("Nova Shield", "Veteran city defender", "🛡️", [
        "Runs triage plans",
        "Mentors new heroes",
      ]),
      character("Flashwire", "Courier-speed teammate", "⚡", [
        "Delivers urgent supplies",
        "Scouts live incidents",
      ]),
      character("Patch", "Tech support genius", "💻", [
        "Tracks villain networks",
        "Builds rapid prototypes",
      ]),
      character("Mayor Quinn", "Public leader under threat", "🏛️", [
        "Coordinates emergency services",
        "Unlocks secure access",
      ]),
    ],
    storyIntros: [
      "At exactly 9:00 PM, every billboard in the city flashes the same symbol, and the power grid dips to red.",
      "A distress ping from the sky rail arrives just as your team briefing starts: multiple cars are stuck over the river.",
    ],
    locations: [
      location("Downtown Grid", "Dense high-rises and power relays", "🌆", [
        "Restore substations",
        "Evacuate block by block",
      ]),
      location("Sky Rail Loop", "Elevated transit line over the harbor", "🚝", [
        "Stabilize train cars",
        "Rescue passengers",
      ]),
      location("Harbor Docks", "Cargo district with hidden labs", "⚓", [
        "Track shipments",
        "Disable smuggling routes",
      ]),
      location("Signal Tower", "Broadcast spire above old town", "📡", [
        "Override rogue transmission",
        "Trace source nodes",
      ]),
    ],
  },
  "Alien Planet": {
    storyNames: [
      "Glowroot Frontier",
      "Visitors in the Fog",
      "The Crystal Orbit",
      "Signals from Below",
    ],
    objectives: [
      objective("Establish peaceful first contact with local species", [
        "Use Xenolinguistics to decode patterns",
        "Use Diplomacy in ritual exchanges",
        "Use Observation to avoid taboo actions",
      ]),
      objective("Secure a safe route through bioluminescent forests", [
        "Use Navigation by star spores",
        "Use Survival with alien weather",
        "Use Botany to identify toxic plants",
      ]),
      objective("Stop a reactor leak at the expedition outpost", [
        "Use Engineering to stabilize cores",
        "Use Teamwork during evacuation",
        "Use Med Support for exposure care",
      ]),
    ],
    storyTwists: [
      "The strangers are descendants of a lost human mission",
      "Glow plants react to emotion, not light",
      "An outpost officer has been altering logs",
      "The planet's storms are controlled by ancient machines",
    ],
    sideQuests: [
      sideQuest("Recover beacon parts from crystal ravine", [
        "Use Climbing anchors",
        "Use Drone scouting",
      ]),
      sideQuest("Trade medicine at a floating village", [
        "Use Cultural protocol",
        "Use Fair negotiation",
      ]),
      sideQuest("Calm territorial grazers near camp", [
        "Use Non-threatening signals",
        "Use Redirect barriers",
      ]),
    ],
    skills: [
      skill("Xenolinguistics", "👽", "Interpret alien speech and symbols"),
      skill("Offworld Survival", "🪐", "Endure unstable climates and terrain"),
      skill("Bio-Scanning", "📡", "Detect hazards and life signatures"),
      skill("Field Engineering", "🔧", "Repair outpost systems quickly"),
      skill("Diplomacy", "🤝", "Build trust with unfamiliar cultures"),
      skill("Rover Piloting", "🚜", "Navigate rough alien landscapes"),
    ],
    characters: [
      character("Commander Sera", "Leader of the survey mission", "🧑‍🚀", [
        "Coordinates rescue ops",
        "Approves contact plans",
      ]),
      character("Tikk", "Curious local guide", "👾", ["Explains customs", "Finds hidden paths"]),
      character("Dr. Han", "Exobiologist", "🔬", ["Analyzes specimens", "Creates antidotes"]),
      character("Unit K-9", "Scout robot", "🤖", ["Maps terrain", "Detects radiation pockets"]),
    ],
    storyIntros: [
      "Your lander touches down beneath glowing trees that pulse in time with your heartbeat.",
      "A translated transmission arrives from nowhere: 'Welcome back, descendants.'",
    ],
    locations: [
      location("Glowroot Basin", "Bioluminescent jungle valley", "🌌", [
        "Follow light patterns",
        "Collect safe samples",
      ]),
      location("Crystal Ravine", "Shimmering canyon with magnetic storms", "💎", [
        "Anchor climbing lines",
        "Avoid charge bursts",
      ]),
      location("Horizon Outpost", "Human research base at planetfall", "🏕️", [
        "Repair life support",
        "Secure perimeter",
      ]),
      location("Skyfloat Village", "Alien settlement hovering on gas bladders", "🎈", [
        "Open diplomatic talks",
        "Trade resources",
      ]),
    ],
  },
  "Wild West": {
    storyNames: [
      "Dustline Oath",
      "Gold Under Red Mesa",
      "Sheriff of Sundown",
      "Rails Through Coyote Pass",
    ],
    objectives: [
      objective("Protect a frontier town from a ruthless gang", [
        "Use Tracking across desert trails",
        "Use Marksmanship in standoffs",
        "Use Leadership to organize townsfolk",
      ]),
      objective("Escort a gold convoy safely through canyon country", [
        "Use Riding for fast response",
        "Use Navigation to avoid ambush spots",
        "Use Negotiation with local ranchers",
      ]),
      objective("Expose the railroad sabotage plot", [
        "Use Investigation at work sites",
        "Use Disguise in saloon meetings",
        "Use Engineering to inspect track faults",
      ]),
    ],
    storyTwists: [
      "The gang leader is trying to reclaim stolen land deeds",
      "The missing gold shipment is decoy cargo",
      "A respected banker funded the sabotage",
      "The outlaws' safehouse is under the sheriff office",
    ],
    sideQuests: [
      sideQuest("Rescue horses during a dust storm", ["Use calm handling", "Use rope work"]),
      sideQuest("Win clues in a poker tournament", ["Use bluff reading", "Use patience"]),
      sideQuest("Repair telegraph lines before night", [
        "Use climbing spikes",
        "Use wire splicing",
      ]),
    ],
    skills: [
      skill("Frontier Tracking", "👣", "Follow wagon and boot trails"),
      skill("Riding", "🐎", "Chase and escort across open plains"),
      skill("Sharpshooting", "🎯", "Take precise shots under pressure"),
      skill("Rope Work", "🪢", "Lasso, secure cargo, and climb"),
      skill("Saloon Diplomacy", "🤠", "Gather intel through conversation"),
      skill("Rail Engineering", "🚂", "Fix and inspect train systems"),
    ],
    characters: [
      character("Sheriff June", "Steady town protector", "⭐", [
        "Knows every ranch trail",
        "Can deputize allies",
      ]),
      character("Doc Mercer", "Medic and inventor", "🩺", [
        "Treats injuries fast",
        "Builds field gadgets",
      ]),
      character("Rosa", "Stagecoach driver", "🛻", ["Finds fastest routes", "Smuggles supplies"]),
      character("Cal", "Ex-outlaw scout", "🧢", ["Reads gang signals", "Maps hidden canyons"]),
    ],
    storyIntros: [
      "A dust cloud rises beyond Sundown Ridge as the noon train fails to arrive.",
      "On your desk: a silver badge, a cracked telegraph, and a warning signed with a coyote mark.",
    ],
    locations: [
      location("Sundown Town", "Frontier settlement around a dry well", "🏜️", [
        "Coordinate defenses",
        "Interview witnesses",
      ]),
      location("Coyote Pass", "Narrow canyon perfect for ambushes", "🪨", [
        "Scout ridge lines",
        "Secure convoy routes",
      ]),
      location("Red Mesa Mine", "Gold dig with unstable shafts", "⛏️", [
        "Inspect tunnels",
        "Recover hidden evidence",
      ]),
      location("Old Telegraph Hill", "Signal station above the valley", "📨", [
        "Restore communications",
        "Spot riders early",
      ]),
    ],
  },
  "Monster Hunters": {
    storyNames: [
      "Lanterns in Hollow Cave",
      "The Gentle Giant",
      "Moonfang Trail",
      "Cryptid Crossing",
    ],
    objectives: [
      objective("Investigate monster sightings without harming local creatures", [
        "Use Tracking on unusual traces",
        "Use Lore to identify species",
        "Use Calm voice techniques",
      ]),
      objective("Protect villages from nighttime cave swarms", [
        "Use Barrier setup around homes",
        "Use Team patrol rotations",
        "Use Signal flares for alarms",
      ]),
      objective("Decide whether to befriend or banish a powerful cryptid", [
        "Use Empathy to read intent",
        "Use Negotiation with locals",
        "Use Defensive gear if needed",
      ]),
    ],
    storyTwists: [
      "The feared monster has been guarding children from a worse threat",
      "Footprints were faked to hide smuggling routes",
      "A village elder knows the creature's true name",
      "The cave noise is caused by ancient machinery",
    ],
    sideQuests: [
      sideQuest("Gather moon herbs for ward candles", ["Use foraging maps", "Avoid toxic patches"]),
      sideQuest("Rescue a scout from bat tunnels", ["Use rope anchors", "Use echo calls"]),
      sideQuest("Catalog three harmless cryptids", ["Use sketch notes", "Use respectful distance"]),
    ],
    skills: [
      skill("Creature Tracking", "🔦", "Follow signs without provoking monsters"),
      skill("Field Lore", "📖", "Identify species and behavior quickly"),
      skill("Trap Craft", "🪤", "Build safe containment tools"),
      skill("Protective Warding", "🕯️", "Set up defensive boundaries"),
      skill("Negotiation", "🤝", "Mediate between townsfolk and hunters"),
      skill("Cave Survival", "🧗", "Move safely in dark underground areas"),
    ],
    characters: [
      character("Ira", "Patient monster ranger", "🧭", [
        "Reads behavior patterns",
        "Prefers non-violent solutions",
      ]),
      character("Soot", "Lantern-bearer apprentice", "🏮", [
        "Navigates cave routes",
        "Spots hidden hazards",
      ]),
      character("Mother Fen", "Village herbalist", "🌿", [
        "Brews repellents",
        "Knows old creature tales",
      ]),
      character("Grumm", "Reformed ogre guide", "🧌", [
        "Lifts heavy obstacles",
        "Speaks giant dialect",
      ]),
    ],
    storyIntros: [
      "At moonrise, three lanterns go out in a row, and the old cave road fills with strange prints.",
      "A worried village sends you a map marked with one word: 'Hollow.'",
    ],
    locations: [
      location("Hollow Cave", "Labyrinth of glowing stone chambers", "🕳️", [
        "Mark safe exits",
        "Track echo signatures",
      ]),
      location("Fogfen Village", "Small settlement beside mist marsh", "🏘️", [
        "Set protective wards",
        "Gather witness reports",
      ]),
      location("Moonfang Ridge", "Rocky highland with old shrines", "🌙", [
        "Search cliff dens",
        "Light signal fires",
      ]),
      location("Cryptid Archive", "Hunter library of creature records", "📚", [
        "Cross-check sightings",
        "Borrow field gear",
      ]),
    ],
  },
  "Tiny World": {
    storyNames: ["Backyard Kingdom", "Antline Express", "The Puddle Ocean", "Button Fort"],
    objectives: [
      objective("Cross the backyard to deliver a peace treaty", [
        "Use Mini Navigation through grass canyons",
        "Use Bug Diplomacy with colony guards",
        "Use Crafting for improvised bridges",
      ]),
      objective("Stop a sprinkler flood from sweeping tiny villages", [
        "Use Engineering to build levees",
        "Use Warning Signals to evacuate",
        "Use Teamwork for sandbag lines",
      ]),
      objective("Recover a lost battery that powers firefly lamps", [
        "Use Climbing on fence walls",
        "Use Tracking near compost heaps",
        "Use Stealth around birds",
      ]),
    ],
    storyTwists: [
      "The flood warning came from a friendly spider",
      "The battery is inside a toy robot patrol",
      "Ant scouts were hiding food for winter, not stealing",
      "A huge bootprint is actually a map key",
    ],
    sideQuests: [
      sideQuest("Repair the leaf-boat ferry", ["Use sap glue", "Use thread rigging"]),
      sideQuest("Escort ladybug medics through pebble pass", [
        "Use hazard markers",
        "Use formation movement",
      ]),
      sideQuest("Recover crumbs from a picnic giant", [
        "Use distraction tactics",
        "Use quick hauling",
      ]),
    ],
    skills: [
      skill("Micro Navigation", "🧭", "Travel through giant everyday terrain"),
      skill("Tiny Engineering", "🔩", "Build tools from scraps and thread"),
      skill("Bug Diplomacy", "🐞", "Cooperate with insect societies"),
      skill("Stealth", "🫥", "Avoid larger predators and humans"),
      skill("Acrobatics", "🤸", "Leap across gaps and raindrops"),
      skill("Scavenging", "🧺", "Find useful items in oversized spaces"),
    ],
    characters: [
      character("Nim", "Mini explorer from Clover Court", "🍀", [
        "Finds hidden shortcuts",
        "Builds travel maps",
      ]),
      character("Brass", "Beetle mechanic", "🪲", ["Fixes tiny machines", "Pulls heavy carts"]),
      character("Penny", "Ant diplomat", "🐜", [
        "Negotiates colony access",
        "Knows tunnel networks",
      ]),
      character("Blink", "Firefly scout", "✨", ["Lights night paths", "Sends coded flashes"]),
    ],
    storyIntros: [
      "From your tiny lookout, a backyard sprinkler looks like a storm cannon preparing to fire.",
      "At dawn, the ant highway stops completely, and trade between tiny towns collapses overnight.",
    ],
    locations: [
      location("Clover Court", "A miniature village under flower leaves", "🌿", [
        "Gather residents",
        "Build emergency shelters",
      ]),
      location("Pebble Pass", "Rocky route between grass towers", "🪨", [
        "Mark safe footholds",
        "Avoid rolling stones",
      ]),
      location("Puddle Ocean", "A vast water hazard after rain", "💧", [
        "Launch leaf boats",
        "Rescue stranded travelers",
      ]),
      location("Button Fort", "Repurposed toy outpost on a porch", "🔘", [
        "Reinforce walls",
        "Signal allied colonies",
      ]),
    ],
  },
  "Pet Detective": {
    storyNames: [
      "Case of the Missing Collar",
      "Whiskers at Midnight",
      "Pawprint on the Window",
      "The Biscuit Burglary",
    ],
    objectives: [
      objective("Solve a string of pet-side neighborhood thefts", [
        "Use Scent Tracking for clue trails",
        "Use Observation at crime scenes",
        "Use Interviewing with local pets",
      ]),
      objective("Find the lost therapy dog before the parade", [
        "Use Network Calls through alley cats",
        "Use Map Reading across blocks",
        "Use Calm approach with frightened witnesses",
      ]),
      objective("Expose the trickster behind fake alarm barks", [
        "Use Pattern Analysis on timing",
        "Use Disguise during stakeouts",
        "Use Team Coordination for capture",
      ]),
    ],
    storyTwists: [
      "The 'thief' was collecting supplies for abandoned pets",
      "A prized collar hides a coded message",
      "The main witness has perfect memory but fear of crowds",
      "The culprit is using remote toy drones",
    ],
    sideQuests: [
      sideQuest("Locate a runaway hamster caravan", [
        "Use tiny-track tracing",
        "Set safe snack stations",
      ]),
      sideQuest("Recover a cat's journal from a tree", [
        "Use climbing gear",
        "Negotiate with crows",
      ]),
      sideQuest("Decode scratch marks behind the bakery", [
        "Compare symbol patterns",
        "Interview night patrol",
      ]),
    ],
    skills: [
      skill("Scent Analysis", "👃", "Separate overlapping scent clues"),
      skill("Pawprint Forensics", "🐾", "Read tracks and movement patterns"),
      skill("Stakeout Stealth", "🕶️", "Observe targets without being seen"),
      skill("Pet Networking", "📞", "Share alerts through neighborhood contacts"),
      skill("Lock and Latch", "🗝️", "Open simple barriers and crates"),
      skill("Interviews", "🗣️", "Ask better questions for cleaner clues"),
    ],
    characters: [
      character("Detective Maple", "Golden retriever lead sleuth", "🐕", [
        "Keeps case files organized",
        "Tracks long scent lines",
      ]),
      character("Velvet", "Sharp-eyed alley cat", "🐈", [
        "Moves across rooftops",
        "Finds hidden stashes",
      ]),
      character("Pico", "Parrot radio operator", "🦜", [
        "Relays messages fast",
        "Mimics suspect voices",
      ]),
      character("Nori", "Rabbit archivist", "🐇", [
        "Stores clues by date",
        "Notices timeline gaps",
      ]),
    ],
    storyIntros: [
      "You arrive at the bakery to find pawprints in flour and one missing box of victory biscuits.",
      "A collar with no name tag appears at HQ, carrying a hidden note stitched inside the lining.",
    ],
    locations: [
      location("Paw Precinct", "Cozy detective office above a pet shop", "🏢", [
        "Review evidence",
        "Assign patrol zones",
      ]),
      location("Midnight Alley", "Narrow lane full of secret routes", "🌙", [
        "Run stakeouts",
        "Track rooftop movement",
      ]),
      location("Biscuit Bakery", "Neighborhood favorite with back-door deliveries", "🥐", [
        "Question staff",
        "Inspect shipment logs",
      ]),
      location("City Park Loop", "Busy walking route with many witnesses", "🌳", [
        "Map pet sightings",
        "Recreate timeline",
      ]),
    ],
  },
  "Arctic Expedition": {
    storyNames: ["Iceheart Compass", "Aurora Station", "The Frozen Vault", "Whiteout Trail"],
    objectives: [
      objective("Reach a lost research camp before supplies run out", [
        "Use Polar Navigation in whiteout",
        "Use Survival to manage heat",
        "Use Team pacing to avoid exhaustion",
      ]),
      objective("Recover an ancient artifact beneath glacier ice", [
        "Use Drilling safety protocols",
        "Use Mapping for crevasse fields",
        "Use Rope rescue readiness",
      ]),
      objective("Protect migrating herds from illegal hunters", [
        "Use Recon scouting",
        "Use Non-violent interception",
        "Use Radio coordination with patrols",
      ]),
    ],
    storyTwists: [
      "The lost camp moved itself to avoid unstable ice",
      "The artifact is a weather stabilizer",
      "A suspected rival team is actually sending help",
      "The blizzard patterns form a coded route",
    ],
    sideQuests: [
      sideQuest("Repair a snowmobile in subzero winds", [
        "Use rapid diagnostics",
        "Use insulated tool handling",
      ]),
      sideQuest("Build emergency shelter before night", [
        "Use snow-block shaping",
        "Use windbreak setup",
      ]),
      sideQuest("Track missing sled dogs", ["Use trail reading", "Use whistle recall"]),
    ],
    skills: [
      skill("Polar Navigation", "🧭", "Travel safely across ice fields"),
      skill("Cold Survival", "❄️", "Manage warmth, food, and shelter"),
      skill("Rescue Rigging", "🪢", "Perform crevasse and cliff rescues"),
      skill("Field Science", "🔬", "Collect stable samples in harsh weather"),
      skill("Expedition Logistics", "📦", "Plan routes and supply drops"),
      skill("Wildlife Stewardship", "🐻", "Protect arctic animals responsibly"),
    ],
    characters: [
      character("Dr. Signe", "Glaciologist expedition head", "🧊", [
        "Reads ice stability",
        "Interprets climate data",
      ]),
      character("Marek", "Veteran guide", "🎿", ["Finds safe passes", "Manages sled teams"]),
      character("Luma", "Drone pilot and mapper", "🛰️", [
        "Scans whiteout routes",
        "Locates stranded crews",
      ]),
      character("Koda", "Rescue dog leader", "🐕", ["Finds buried gear", "Tracks scent in wind"]),
    ],
    storyIntros: [
      "The aurora flickers overhead as your radio catches a half-message from a camp thought lost two winters ago.",
      "A crack races through the glacier beneath your boots, revealing carved symbols under the ice.",
    ],
    locations: [
      location("Aurora Station", "Remote lab under dancing northern lights", "🌌", [
        "Decode last transmissions",
        "Refit expedition gear",
      ]),
      location("Frostbite Ridge", "Wind-scoured mountain pass", "🏔️", [
        "Set rope lines",
        "Spot avalanche zones",
      ]),
      location("Glacier Vault", "Sub-ice chamber with ancient machinery", "🧊", [
        "Stabilize entry tunnel",
        "Document artifacts",
      ]),
      location("Seal Bay", "Coastal ice shelf and wildlife route", "🦭", [
        "Monitor migration",
        "Intercept poachers",
      ]),
    ],
  },
  "Robot City": {
    storyNames: [
      "Circuit Parade",
      "The Missing Mainframe",
      "Neon Service Run",
      "Gearline Uprising",
    ],
    objectives: [
      objective("Restore city systems after a cascade outage", [
        "Use Diagnostics to locate failures",
        "Use Coding to patch service loops",
        "Use Drone ops to reach high relays",
      ]),
      objective("Find the vanished city architect AI core", [
        "Use Data tracing across subnet tunnels",
        "Use Negotiation with worker bots",
        "Use Security bypass in closed sectors",
      ]),
      objective("Prevent factory overclock from damaging residents", [
        "Use Engineering to cool reactors",
        "Use Team routing to evacuate zones",
        "Use Command protocols to halt lines",
      ]),
    ],
    storyTwists: [
      "The missing AI core left intentionally to avoid corruption",
      "Outage spikes are caused by outdated human firmware",
      "Factory bots are striking for fair maintenance",
      "A tiny helper drone contains master keys",
    ],
    sideQuests: [
      sideQuest("Recalibrate schoolbot learning servers", [
        "Use safe reboot cycles",
        "Validate data backups",
      ]),
      sideQuest("Retrieve lost memory chip from scrap district", [
        "Use magnet sweeps",
        "Decode salvage tags",
      ]),
      sideQuest("Escort med-bot convoy through traffic grid", [
        "Override signal loops",
        "Clear blocked lanes",
      ]),
    ],
    skills: [
      skill("System Diagnostics", "🧠", "Identify and isolate digital faults"),
      skill("Circuit Repair", "🔧", "Fix hardware in field conditions"),
      skill("Code Patching", "💻", "Apply stable software fixes quickly"),
      skill("Drone Operations", "🛸", "Deploy scouts and utility drones"),
      skill("Bot Mediation", "🤝", "Resolve conflicts between robot factions"),
      skill("Grid Navigation", "🗺️", "Travel efficiently across smart streets"),
    ],
    characters: [
      character("Unit Ada", "City maintenance coordinator", "🤖", [
        "Accesses infrastructure maps",
        "Allocates repair teams",
      ]),
      character("Bolt", "Courier drone ace", "🚁", [
        "Delivers parts quickly",
        "Scouts blocked sectors",
      ]),
      character("Iris", "Human coder liaison", "👩‍💻", [
        "Writes emergency patches",
        "Translates old protocols",
      ]),
      character("Forge-9", "Factory forebot", "🏭", [
        "Controls assembly lines",
        "Knows plant secrets",
      ]),
    ],
    storyIntros: [
      "At sunrise, Robot City falls silent as every service bot pauses mid-task for exactly nine seconds.",
      "A maintenance tunnel map arrives with one note: 'Find the core before midnight reboot.'",
    ],
    locations: [
      location("Central Grid", "Main power district and relay towers", "⚡", [
        "Stabilize energy flow",
        "Patch control hubs",
      ]),
      location("Gearline Works", "Massive automated factory ring", "🏭", [
        "Cool overclocked units",
        "Inspect production nodes",
      ]),
      location("Data Canals", "Fiber routes under the city", "🌐", [
        "Trace packet routes",
        "Recover lost logs",
      ]),
      location("Spark Plaza", "Public square for bot assemblies", "🟦", [
        "Host negotiations",
        "Broadcast city updates",
      ]),
    ],
  },
  "Steampunk Voyages": {
    storyNames: [
      "The Brass Horizon",
      "Clocktower Armada",
      "Steam over Sapphire",
      "Goggles and Gale",
    ],
    objectives: [
      objective("Chart an airship route through storm belts", [
        "Use Air Navigation by barometer",
        "Use Engine Tuning for pressure shifts",
        "Use Crew Coordination during squalls",
      ]),
      objective("Recover stolen plans for a peace engine", [
        "Use Espionage in guild districts",
        "Use Lockcraft on vault mechanisms",
        "Use Diplomacy with rival captains",
      ]),
      objective("Prevent a clockwork city from freezing at dawn", [
        "Use Mechanic skills on gear cores",
        "Use Power routing across steam lines",
        "Use Fast repairs on tower valves",
      ]),
    ],
    storyTwists: [
      "The stolen plans were incomplete decoys",
      "A pirate captain is protecting refugees",
      "City clocks are synced to an ancient comet",
      "The saboteur is a former mentor",
    ],
    sideQuests: [
      sideQuest("Refit balloon envelopes in heavy wind", [
        "Use rigging knots",
        "Use pressure checks",
      ]),
      sideQuest("Deliver diplomatic tea crates by glider", [
        "Use rooftop landing",
        "Avoid patrol spotlights",
      ]),
      sideQuest("Decode guild seals from brass coins", [
        "Use magnifier study",
        "Cross-check mint marks",
      ]),
    ],
    skills: [
      skill("Airship Piloting", "🛩️", "Steer through weather fronts and traffic lanes"),
      skill("Clockwork Mechanics", "⚙️", "Repair gears, valves, and automata"),
      skill("Steam Engineering", "♨️", "Balance boilers and pressure lines"),
      skill("Guild Diplomacy", "🎩", "Negotiate with industrial factions"),
      skill("Rooftop Mobility", "🧗", "Traverse city heights quickly"),
      skill("Cipher Reading", "🗝️", "Interpret encoded blueprints and logs"),
    ],
    characters: [
      character("Captain Vale", "Airship commander", "🧭", [
        "Plots safe cloud lanes",
        "Keeps crew disciplined",
      ]),
      character("Mina Brass", "Inventor with genius prototypes", "🔩", [
        "Builds emergency tools",
        "Improves engine efficiency",
      ]),
      character("Inspector Hobb", "City clock inspector", "🕰️", [
        "Knows tower layouts",
        "Detects sabotage",
      ]),
      character("Pip Gearfoot", "Courier acrobat", "🕶️", [
        "Runs rooftop messages",
        "Opens hidden passages",
      ]),
    ],
    storyIntros: [
      "Your airship moorings snap at dawn, and a coded signal blinks from the grand clocktower.",
      "In the brass market, someone slips a blueprint fragment into your coat with a warning: 'They'll stop the city clock.'",
    ],
    locations: [
      location("Aether Docks", "Floating airship harbor above the city", "⚓", [
        "Refuel and refit",
        "Recruit crew",
      ]),
      location("Grand Clocktower", "Heart of the city's timing grid", "🕰️", [
        "Repair gear trains",
        "Trace sabotage points",
      ]),
      location("Brass Bazaar", "Busy guild market full of contraptions", "🛍️", [
        "Buy rare parts",
        "Gather rumors",
      ]),
      location("Steamline Quarter", "Industrial district of pipes and valves", "🏭", [
        "Reroute steam pressure",
        "Secure boiler rooms",
      ]),
    ],
  },
  "Mad Lab": {
    storyNames: [
      "The Rogue Prototype",
      "Lab Seven Alarm",
      "Quantum Jelly",
      "Operation: Undo Button",
    ],
    objectives: [
      objective("Contain experimental inventions before they escape campus", [
        "Use Safety Protocols in active labs",
        "Use Robotics to disable rogue bots",
        "Use Teamwork in containment squads",
      ]),
      objective("Reverse a formula that mutates everyday objects", [
        "Use Chemistry to craft antidote",
        "Use Data logs to trace source",
        "Use Testing to verify stability",
      ]),
      objective("Restore power to the clean-room wing", [
        "Use Circuit repair on fuse arrays",
        "Use Calm planning under alarms",
        "Use Communication for coordinated reset",
      ]),
    ],
    storyTwists: [
      "The rogue robot is protecting test animals",
      "The mutation formula came from a mislabeled juice box",
      "A janitor solved half the puzzle first",
      "The emergency AI locked doors to prevent a bigger blast",
    ],
    sideQuests: [
      sideQuest("Catch escaped nano-cleaners", ["Use magnetic traps", "Seal ventilation shafts"]),
      sideQuest("Recover frozen samples from cryo wing", [
        "Use thermal gloves",
        "Follow frost-safe route",
      ]),
      sideQuest("Fix lab cafeteria auto-chef", [
        "Reflash recipe firmware",
        "Calibrate heat sensors",
      ]),
    ],
    skills: [
      skill("Experimental Chemistry", "🧪", "Mix volatile compounds safely"),
      skill("Robot Taming", "🤖", "Disable or reprogram rogue machines"),
      skill("Lab Safety", "🧯", "Respond fast to hazardous incidents"),
      skill("Circuit Repair", "🔌", "Restore failing power systems"),
      skill("Data Analysis", "📊", "Find anomalies in experiment logs"),
      skill("Rapid Prototyping", "🛠️", "Build solutions from spare parts"),
    ],
    characters: [
      character("Dr. Flux", "Brilliant but chaotic lead scientist", "🥼", [
        "Understands prototype logic",
        "Approves emergency overrides",
      ]),
      character("Kei", "Safety officer", "🧯", [
        "Runs evacuation routes",
        "Contains hazardous spills",
      ]),
      character("Byte", "Helpful lab robot", "🤖", [
        "Accesses maintenance shafts",
        "Carries heavy equipment",
      ]),
      character("Mara", "Intern with pattern memory", "🧠", [
        "Finds hidden lab links",
        "Spots mislabeled samples",
      ]),
    ],
    storyIntros: [
      "An alarm marked 'Level Orange' blares as your breakfast spoon starts levitating.",
      "Lab Seven doors lock down automatically, and a text appears on every monitor: 'Prototype awake.'",
    ],
    locations: [
      location("Lab Seven", "High-risk experiment chamber", "🧪", [
        "Contain unstable devices",
        "Run emergency diagnostics",
      ]),
      location("Cryo Wing", "Subzero storage vaults", "🧊", [
        "Protect sensitive samples",
        "Restore cooling loops",
      ]),
      location("Prototype Garage", "Workshop for test machines", "🛠️", [
        "Reprogram bots",
        "Swap damaged modules",
      ]),
      location("Data Core", "Server room for all experiment logs", "💾", [
        "Trace error chains",
        "Patch security gates",
      ]),
    ],
  },
  "Time Tangles": {
    storyNames: [
      "Clockloop Harbor",
      "The Paradox Pirate",
      "Yesterday's Letter",
      "The Broken Timeline",
    ],
    objectives: [
      objective("Close a time loop before history collapses", [
        "Use Chronology Mapping for event chains",
        "Use Observation for repeating clues",
        "Use Decision control to avoid paradoxes",
      ]),
      objective("Return stolen artifacts to their proper eras", [
        "Use Era Knowledge to identify origins",
        "Use Stealth in guarded museums",
        "Use Diplomacy across time factions",
      ]),
      objective("Stop paradox pirates from rewriting city history", [
        "Use Tactical planning at timeline forks",
        "Use Gadget timing for portal windows",
        "Use Team sync during jumps",
      ]),
    ],
    storyTwists: [
      "Your mysterious helper is your future self",
      "The pirates are trying to fix a worse timeline",
      "One artifact chooses its own era",
      "A small decision in the past saves the present",
    ],
    sideQuests: [
      sideQuest("Find a missing minute from the city clock", [
        "Trace temporal residue",
        "Align clock gears",
      ]),
      sideQuest("Escort a historian through two centuries", [
        "Use era disguise",
        "Avoid timeline contact",
      ]),
      sideQuest("Repair a portal anchor", ["Recalibrate coils", "Stabilize energy spikes"]),
    ],
    skills: [
      skill("Chronology Mapping", "⏳", "Track branching timeline events"),
      skill("Temporal Navigation", "🧭", "Jump safely between periods"),
      skill("Paradox Prevention", "🛑", "Avoid actions that break causality"),
      skill("Era Adaptation", "👒", "Blend into historical settings"),
      skill("Archive Research", "📚", "Verify historical outcomes quickly"),
      skill("Portal Engineering", "🌀", "Repair and stabilize time gates"),
    ],
    characters: [
      character("Agent Noon", "Time bureau field lead", "🕰️", [
        "Coordinates jump windows",
        "Knows timeline laws",
      ]),
      character("Patch 47", "Clockwork navigator", "⚙️", [
        "Tunes portal machinery",
        "Predicts loop behavior",
      ]),
      character("Captain Vex", "Paradox pirate", "🏴‍☠️", [
        "Knows hidden timelines",
        "Offers uneasy alliances",
      ]),
      character("Eli", "Teen historian", "📜", [
        "Spots anachronisms fast",
        "Finds era-specific clues",
      ]),
    ],
    storyIntros: [
      "You wake to the same sunrise three days in a row, each time with one extra crack in the town clock.",
      "A letter postmarked 1892 arrives with your own handwriting and one instruction: 'Don't trust noon bells.'",
    ],
    locations: [
      location("Clockloop Harbor", "Port where time tides overlap", "⏰", [
        "Monitor timeline drift",
        "Intercept paradox ships",
      ]),
      location("Bureau Archive", "Vault of protected historical records", "📚", [
        "Verify timeline changes",
        "Retrieve era permits",
      ]),
      location("Forkpoint Alley", "Street where multiple histories collide", "🛣️", [
        "Choose branch paths",
        "Set temporal beacons",
      ]),
      location("Rift Engine Room", "Core machinery for time gates", "🌀", [
        "Repair anchors",
        "Control jump intervals",
      ]),
    ],
  },
  "Cyberpunk Streets": {
    storyNames: ["Neon Drift", "Ghost in the Grid", "Subway Heist", "Chrome Dawn"],
    objectives: [
      objective("Expose a corporation draining district power", [
        "Use Hacking to access closed systems",
        "Use Stealth across surveillance zones",
        "Use Evidence gathering for public release",
      ]),
      objective("Rescue a coder kidnapped for encryption keys", [
        "Use Network intel for target location",
        "Use Mobility through back-alley routes",
        "Use Tactical breach for extraction",
      ]),
      objective("Stop malware spreading through city implants", [
        "Use Cybersecurity to isolate nodes",
        "Use Medical support for affected citizens",
        "Use Team comms under signal jamming",
      ]),
    ],
    storyTwists: [
      "The malware originated from an old civic safety system",
      "Your inside contact is a corporate executive's child",
      "The kidnapped coder launched the leak intentionally",
      "A street gang has been protecting neighborhoods quietly",
    ],
    sideQuests: [
      sideQuest("Reboot community clinic firewall", [
        "Patch legacy code",
        "Monitor attack traffic",
      ]),
      sideQuest("Retrieve stolen battery cells", [
        "Track black-market listing",
        "Intercept courier route",
      ]),
      sideQuest("Scrub false warrants from police net", [
        "Bypass auth layers",
        "Verify citizen records",
      ]),
    ],
    skills: [
      skill("Hacking", "💻", "Break into secure digital systems"),
      skill("Street Stealth", "🕶️", "Move unseen through monitored zones"),
      skill("Cyber Forensics", "🔍", "Trace attacks back to their source"),
      skill("Augment Tuning", "🔧", "Adjust and repair cybernetic gear"),
      skill("Drone Piloting", "🛸", "Scout and relay from the air"),
      skill("District Networking", "📡", "Coordinate allies across city blocks"),
    ],
    characters: [
      character("Ryn", "Freelance netrunner", "🧑‍💻", [
        "Cracks secure doors",
        "Builds stealth scripts",
      ]),
      character("Vera", "Clinic mechanic", "🦾", ["Repairs implants", "Keeps med systems online"]),
      character("Ghost", "Anonymous informant", "🎭", [
        "Feeds encrypted leads",
        "Knows corp schedules",
      ]),
      character("Jax", "Courier on mag-bike", "🏍️", [
        "Runs high-speed deliveries",
        "Navigates blocked sectors",
      ]),
    ],
    storyIntros: [
      "Neon rain hisses on the pavement as every public screen displays a single hacked phrase: 'Power belongs to the people.'",
      "Your comm implant crackles with a priority ping and a map to a subway station that officially does not exist.",
    ],
    locations: [
      location("Neon Market", "Crowded district of stalls and holo-signs", "🌃", [
        "Gather street intel",
        "Find rare components",
      ]),
      location("Gridline Subway", "Abandoned transit tunnels under downtown", "🚇", [
        "Run covert routes",
        "Hide from drone sweeps",
      ]),
      location("Apex Spire", "Corporate megatower with layered security", "🏙️", [
        "Infiltrate data vault",
        "Upload evidence",
      ]),
      location("Patchwork Clinic", "Community med center in old warehouse", "🏥", [
        "Treat malware cases",
        "Secure patient records",
      ]),
    ],
  },
  "Secret Agents": {
    storyNames: [
      "Operation Glass Key",
      "Midnight Briefing",
      "The Silent Embassy",
      "Project Sparrow",
    ],
    objectives: [
      objective("Recover a stolen device before global summit", [
        "Use Surveillance to track handlers",
        "Use Disguise in secure venues",
        "Use Tactical entry for clean extraction",
      ]),
      objective("Protect a whistleblower crossing hostile territory", [
        "Use Route planning for safe transit",
        "Use Counterintelligence to avoid tails",
        "Use Defensive driving during pursuit",
      ]),
      objective("Uncover the mole inside headquarters", [
        "Use Pattern analysis on message leaks",
        "Use Interviewing under cover identities",
        "Use Cyber checks on access logs",
      ]),
    ],
    storyTwists: [
      "The stolen device is a decoy with hidden tracker",
      "The suspected mole has been feeding false intel",
      "An enemy agent offers genuine help",
      "The mission target is linked to your training class",
    ],
    sideQuests: [
      sideQuest("Swap bugged briefcase at train terminal", [
        "Use timing windows",
        "Use crowd blending",
      ]),
      sideQuest("Decrypt one-time-pad message", ["Align cipher strips", "Verify keyword chain"]),
      sideQuest("Extract teammate from gala event", ["Use social cover", "Use silent comms"]),
    ],
    skills: [
      skill("Disguise", "🎭", "Assume believable identities quickly"),
      skill("Surveillance", "📹", "Track targets without detection"),
      skill("Cipher Work", "🗝️", "Decode and encode secure messages"),
      skill("Counterintelligence", "🕵️", "Detect and mislead hostile spies"),
      skill("Infiltration", "🧗", "Enter restricted spaces quietly"),
      skill("Mission Command", "🧠", "Coordinate complex field operations"),
    ],
    characters: [
      character("Director Hale", "Agency mission lead", "🕶️", [
        "Approves mission parameters",
        "Manages diplomatic risk",
      ]),
      character("Kite", "Field agent and master of cover", "🧥", [
        "Builds identities",
        "Runs embassy contacts",
      ]),
      character("Nova", "Signal analyst", "📻", [
        "Intercepts coded traffic",
        "Monitors operation channels",
      ]),
      character("Brick", "Extraction specialist", "🚗", [
        "Handles pursuit escapes",
        "Secures exfil routes",
      ]),
    ],
    storyIntros: [
      "Your watch vibrates once: a silent summons to the agency vault beneath the old opera house.",
      "At midnight briefing, your handler slides a photo across the table: someone inside headquarters is selling missions.",
    ],
    locations: [
      location("Agency Vault", "Underground command center", "🏢", [
        "Review mission files",
        "Gear up discreetly",
      ]),
      location("Silent Embassy", "High-security diplomatic compound", "🏛️", [
        "Infiltrate reception",
        "Gather classified intel",
      ]),
      location("Terminal Nine", "Transit hub crowded with operatives", "🚉", [
        "Swap packages",
        "Shadow suspects",
      ]),
      location("Raven Safehouse", "Hidden apartment for covert debriefs", "🏠", [
        "Lay low",
        "Cross-check evidence",
      ]),
    ],
  },
};

export const AVAILABLE_THEMES = Object.keys(THEME_CONTENT);

// Helper to pick multiple unique items randomly
export function pickMultipleUnique<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, arr.length));
}
